import { restApiKey, table, viewId } from '../../../../_constants/Code'
import { errorLog } from '../../../../common/logger/Logger'
import { PathInfoList } from '../../../../_constants/PathInfoList'
import { getDynamoDB } from '../../../../common/utils/DynamoDbUtils'
import { httpGet } from '../../../../services/api/AmplifyApi'
import { mapApiItemsToEmployeeStatusData } from './apiMapper'

/** 会社種別 */
export const CompanyTypeEnum = Object.freeze({
  STANDALONE: 'STANDALONE',
  SUB: 'SUB',
  SUPER: 'SUPER'
})

/**
 * 子会社か（SuperCorporationNumber が自法人番号と異なる値で設定されている）
 *
 * @param {string | null | undefined} superCorporationNumber
 * @param {string | null | undefined} corporationNumber
 * @returns {boolean}
 */
export function isSubsidiaryCompany(superCorporationNumber, corporationNumber) {
  const superCorp = (superCorporationNumber ?? '').trim()
  if (!superCorp) {
    return false
  }
  return superCorp !== (corporationNumber ?? '').trim()
}

/** 10.6.1 画面向けメッセージ */
export const EMPLOYEE_STATUS_MESSAGES = {
  configNotRegistered:
    '有価証券報告書レポート設定が未登録です。「有価証券報告書レポート設定」画面から設定を登録してください。',
  fetchFailed: 'データの取得に失敗しました'
}

const SECURITIES_REPORT_CONFIG_NOT_FOUND_API_MESSAGE = '有価証券報告書設定が見つかりません'

/**
 * IF02103 エラーレスポンスから API メッセージを取得する
 *
 * @param {unknown} error
 * @returns {string}
 */
function extractApiErrorMessage(error) {
  const message =
    error?.response?.data?.body?.message ?? error?.response?.data?.message ?? error?.response?.body?.message

  if (Array.isArray(message)) {
    return message[0] ?? ''
  }

  return typeof message === 'string' ? message : ''
}

/**
 * 従業員の状況データ取得エラーを画面表示用メッセージへ変換する
 *
 * @param {unknown} error
 * @returns {string}
 */
export function resolveEmployeeStatusErrorMessage(error) {
  const apiMessage = extractApiErrorMessage(error)

  if (apiMessage === SECURITIES_REPORT_CONFIG_NOT_FOUND_API_MESSAGE) {
    return EMPLOYEE_STATUS_MESSAGES.configNotRegistered
  }

  return EMPLOYEE_STATUS_MESSAGES.fetchFailed
}

/**
 * 提出会社の状況に含める子会社をフィルタ（将来 API / マスタ設定連携用）
 *
 * @param {import('./types').SubmitterRow[]} submitter
 * @param {string[] | null} includedCompanyNames Null の場合は全社表示
 * @returns {import('./types').SubmitterRow[]}
 */
export function filterSubmitterRows(submitter, includedCompanyNames = null) {
  if (includedCompanyNames === null) {
    return submitter
  }
  return submitter.filter((row) => row.kind === 'submit' || includedCompanyNames.includes(row.name))
}

/**
 * IF02103 有価証券報告書集計情報取得
 *
 * @param {string} corporationNumber
 * @returns {Promise<object[]>}
 */
async function fetchSecuritiesReportSummaryItems(corporationNumber) {
  const init = {
    headers: {},
    queryStringParameters: {}
  }
  const response = await httpGet(
    restApiKey.CHROFY_REST_API,
    PathInfoList.Resource.getSecuritiesReportSummary(corporationNumber),
    init
  )
  return response?.Items ?? []
}

/**
 * 会社種別を判定する
 *
 * 1. SuperCorporationNumber が自法人番号と異なる → 子会社
 * 2. それ以外 → AnalysisTargetInfo あり → 親会社 / なし → 単独会社
 *
 * @param {string | null | undefined} superCorporationNumber
 * @param {string | null | undefined} corporationNumber
 * @param {boolean} hasAnalysisTarget
 * @returns {(typeof CompanyTypeEnum)[keyof typeof CompanyTypeEnum]}
 */
export function determineCompanyType(superCorporationNumber, corporationNumber, hasAnalysisTarget) {
  if (isSubsidiaryCompany(superCorporationNumber, corporationNumber)) {
    return CompanyTypeEnum.SUB
  }
  if (!hasAnalysisTarget) {
    return CompanyTypeEnum.STANDALONE
  }
  return CompanyTypeEnum.SUPER
}

/**
 * ① 連結会社の状況を表示するか
 *
 * 子会社は常に非表示。親会社は表示。単独会社判定時はログイン時 isParent を参照する。
 *
 * @param {(typeof CompanyTypeEnum)[keyof typeof CompanyTypeEnum]} companyType
 * @param {boolean | undefined} isParent ログイン時 AnalysisTargetInfo から設定
 * @returns {boolean}
 */
export function resolveShowConsolidatedSection(companyType, isParent) {
  if (companyType === CompanyTypeEnum.SUB) {
    return false
  }
  if (companyType === CompanyTypeEnum.SUPER) {
    return true
  }
  return isParent === true
}

/**
 * Company / AnalysisTargetInfo から会社種別を取得
 *
 * @param {string} corporationNumber
 * @returns {Promise<(typeof CompanyTypeEnum)[keyof typeof CompanyTypeEnum]>}
 */
async function resolveCompanyType(corporationNumber) {
  try {
    const [analysisTarget, companyRows] = await Promise.all([
      getDynamoDB(table.AnalysisTargetInfo, null, corporationNumber),
      getDynamoDB(table.Company, null, corporationNumber)
    ])
    const companyData = Array.isArray(companyRows) ? companyRows[0] : companyRows
    const hasAnalysisTarget = Array.isArray(analysisTarget) && analysisTarget.length > 0

    return determineCompanyType(
      companyData?.SuperCorporationNumber,
      companyData?.CorporationNumber ?? corporationNumber,
      hasAnalysisTarget
    )
  } catch (error) {
    errorLog('E000001', '', viewId.legalDisclosure, 'resolveCompanyType', error)
    return CompanyTypeEnum.STANDALONE
  }
}

/**
 * 従業員の状況データ取得（IF02103 + 会社種別）
 *
 * @param {{ corporationNumber?: string; isParent?: boolean }} props QuickSight から渡される props
 * @returns {Promise<{
 *   yearOptions: string[]
 *   dataByPeriod: Record<string, import('./types').EmployeeStatusYearData>
 *   showConsolidatedSection: boolean
 * }>}
 */
export async function fetchEmployeeStatusData(props) {
  const corporationNumber = String(props?.corporationNumber ?? '').trim()
  if (!corporationNumber) {
    throw new Error('法人番号が取得できません')
  }

  try {
    const [items, companyType] = await Promise.all([
      fetchSecuritiesReportSummaryItems(corporationNumber),
      resolveCompanyType(corporationNumber)
    ])
    return {
      ...mapApiItemsToEmployeeStatusData(items),
      showConsolidatedSection: resolveShowConsolidatedSection(companyType, props.isParent)
    }
  } catch (error) {
    errorLog('E000001', '', viewId.legalDisclosure, 'fetchEmployeeStatusData', error)
    throw error
  }
}

/**
 * 提出会社行のソート用合計従業員数（本体 + 臨時）
 *
 * @param {import('./types').SubmitterRow} row
 * @returns {number}
 */
export function getTotalEmployeeCount(row) {
  const match = (row.employeesDisplay || '').match(/\(([\d,]+)\)/)
  const tempEmp = match ? parseInt(match[1].replace(/,/g, ''), 10) : 0
  return row.employees + tempEmp
}

/**
 * 提出会社行をソート（提出会社は常に最上位固定）
 *
 * @param {import('./types').SubmitterRow[]} rows
 * @param {'asc' | 'desc'} direction
 * @returns {import('./types').SubmitterRow[]}
 */
export function sortSubmitterRows(rows, direction) {
  const fixed = rows.filter((r) => r.kind === 'submit')
  const sortable = [...rows.filter((r) => r.kind !== 'submit')].sort((a, b) => {
    const av = getTotalEmployeeCount(a)
    const bv = getTotalEmployeeCount(b)
    return direction === 'asc' ? av - bv : bv - av
  })
  return [...fixed, ...sortable]
}

/**
 * 表示対象年度にエクスポート可能なデータが1件以上あるか
 *
 * @param {import('./types').EmployeeStatusYearData | null | undefined} yearData
 * @param {boolean} [showConsolidatedSection=true] Default is `true`
 * @returns {boolean}
 */
export function hasEmployeeStatusDisplayData(yearData, showConsolidatedSection = true) {
  if (!yearData) {
    return false
  }
  return (
    (showConsolidatedSection && (yearData.consolidated?.rows?.length ?? 0) > 0) ||
    (yearData.submitter?.length ?? 0) > 0 ||
    (yearData.segmentBreakdown?.rows?.length ?? 0) > 0 ||
    (yearData.diversity?.length ?? 0) > 0
  )
}
